import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Plus, Trash2, TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { rowsOf, useApi } from '@/Hooks/useApi';
import { useCurrency } from '@/Hooks/useCurrency';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableEmpty, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { apiMessage, formatQuantity } from '../shared';
import { RecipeComponentPicker } from './RecipeComponentPicker';
import {
  fractionToPercent,
  isDecimalWithScale,
  isZeroDecimal,
  percentToFraction,
  trimDecimal,
} from './decimalString';
import type {
  ComponentPayload,
  ComponentTarget,
  CostPreview,
  RecipeComponent,
  RecipeVersion,
  UnitOption,
  VersionPayload,
} from './recipeTypes';

interface Props {
  open: boolean;
  recipeId: string;
  recipeName: string;
  /** The DRAFT being edited. `null` authors a brand-new draft. */
  draft: RecipeVersion | null;
  /** A published version whose components seed the new draft, if any. */
  seed: RecipeVersion | null;
  onClose: () => void;
  onSaved: () => void;
}

/** A component row while it is being edited. One target id, never two. */
interface Row {
  key: string;
  targetKind: 'ITEM' | 'SUB';
  targetId: string;
  unitId: string;
  /** Decimal text at 3dp, exactly as the API will receive it. */
  quantity: string;
  /** Wastage as a percentage, as people say it: `5` means 5%. */
  wastage: string;
  isOptional: boolean;
}

const targetKey = (kind: 'ITEM' | 'SUB', id: string) => `${kind}:${id}`;

let rowSeq = 0;
const nextKey = () => `row-${(rowSeq += 1)}`;

function rowFromComponent(component: RecipeComponent): Row {
  return {
    key: component.id || nextKey(),
    targetKind: component.subRecipe ? 'SUB' : 'ITEM',
    targetId: component.subRecipe?.id ?? component.inventoryItem?.id ?? '',
    unitId: component.unit?.id ?? '',
    quantity: trimDecimal(component.quantity),
    wastage: fractionToPercent(component.wastagePercent),
    isOptional: component.isOptional,
  };
}

function targetsFromComponents(components: RecipeComponent[]): Map<string, ComponentTarget> {
  const map = new Map<string, ComponentTarget>();
  for (const component of components) {
    if (component.subRecipe) {
      map.set(targetKey('SUB', component.subRecipe.id), {
        kind: 'SUB',
        id: component.subRecipe.id,
        name: component.subRecipe.name,
        // The saved line does not carry the sub-recipe's yield unit; it is read
        // separately, and until it lands the unit list stays locked.
        unitId: null,
        resolved: false,
      });
    } else if (component.inventoryItem) {
      map.set(targetKey('ITEM', component.inventoryItem.id), {
        kind: 'ITEM',
        id: component.inventoryItem.id,
        name: component.inventoryItem.name,
        unitId: component.inventoryItem.unitId ?? null,
        resolved: true,
        hint: component.inventoryItem.sku ?? undefined,
      });
    }
  }
  return map;
}

/**
 * §21 — authoring one revision of a bill of materials.
 *
 * Four rules shape this form, and each is enforced structurally rather than by
 * hoping the user reads a hint:
 *
 *  1. **A line is an inventory item xor a sub-recipe.** The picker returns one
 *     tagged target and the row keeps one id, so both can never be set.
 *  2. **A quantity may only be entered in a unit that converts.** The unit list
 *     is filtered to the `UnitType` of the target's own unit, so "3 litres of
 *     flour" is not on the menu.
 *  3. **Only a DRAFT is editable.** A published version opens as a *new draft*
 *     copied from it, because historic COGS stays pinned to the version that was
 *     active at sale time.
 *  4. **Decimals are text.** Quantities carry 3dp and costs 4dp; nothing here
 *     parses a decimal into a float — see `decimalString.ts`.
 */
export function RecipeVersionEditor({
  open,
  recipeId,
  recipeName,
  draft,
  seed,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const { format } = useCurrency();

  const [rows, setRows] = useState<Row[]>([]);
  const [targets, setTargets] = useState<Map<string, ComponentTarget>>(new Map());
  const [yieldQuantity, setYieldQuantity] = useState('1');
  const [yieldUnitId, setYieldUnitId] = useState('');
  const [notes, setNotes] = useState('');

  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [includeOptional, setIncludeOptional] = useState(false);
  const [preview, setPreview] = useState<CostPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const previewSeq = useRef(0);
  const attempted = useRef(new Set<string>());

  const unitsApi = useApi<unknown>(open ? '/inventory/units' : null, {
    limit: 200,
    isActive: true,
    sortBy: 'code',
    sortOrder: 'asc',
  });
  const units = useMemo(() => rowsOf<UnitOption>(unitsApi.data).rows, [unitsApi.data]);
  const unitById = useMemo(() => new Map(units.map((unit) => [unit.id, unit])), [units]);

  // ------------------------------------------------------------------ seeding

  useEffect(() => {
    if (!open) return;
    const source = draft ?? seed;
    const components = source?.components ?? [];
    attempted.current = new Set();
    setRows(components.map(rowFromComponent));
    setTargets(targetsFromComponents(components));
    setYieldQuantity(source ? trimDecimal(source.yieldQuantity) : '1');
    setYieldUnitId(source?.yieldUnit?.id ?? '');
    setNotes(draft?.notes ?? seed?.notes ?? '');
    setPickerFor(null);
    setPreview(null);
    setPreviewError(null);
    setError(null);
  }, [open, draft, seed]);

  /**
   * A sub-recipe copied from a saved line arrives without its yield unit, and
   * without it the unit filter has nothing to filter by. One read per distinct
   * sub-recipe, once.
   */
  useEffect(() => {
    const pending = [...targets.values()].filter(
      (target) => target.kind === 'SUB' && !target.resolved && !attempted.current.has(target.id),
    );
    if (!pending.length) return;
    for (const target of pending) attempted.current.add(target.id);

    let cancelled = false;
    void Promise.all(
      pending.map(async (target) => {
        try {
          const { data } = await api.get<{ activeVersion?: { yieldUnitId?: string | null } | null }>(
            `/recipes/${target.id}`,
          );
          return { id: target.id, unitId: data.activeVersion?.yieldUnitId ?? null };
        } catch {
          return { id: target.id, unitId: null };
        }
      }),
    ).then((resolved) => {
      if (cancelled) return;
      setTargets((previous) => {
        const next = new Map(previous);
        for (const entry of resolved) {
          const key = targetKey('SUB', entry.id);
          const target = next.get(key);
          if (target) next.set(key, { ...target, unitId: entry.unitId, resolved: true });
        }
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [targets]);

  // ---------------------------------------------------------------- row edits

  const targetOf = useCallback(
    (row: Row) => targets.get(targetKey(row.targetKind, row.targetId)),
    [targets],
  );

  /** Only units that convert into the target's own unit — same `UnitType`. */
  const unitsFor = useCallback(
    (row: Row): UnitOption[] => {
      const natural = targetOf(row)?.unitId;
      const type = natural ? unitById.get(natural)?.type : undefined;
      return type ? units.filter((unit) => unit.type === type) : [];
    },
    [targetOf, unitById, units],
  );

  const patchRow = (key: string, patch: Partial<Row>) =>
    setRows((previous) => previous.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const moveRow = (index: number, delta: number) =>
    setRows((previous) => {
      const next = [...previous];
      const target = index + delta;
      if (target < 0 || target >= next.length) return previous;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const applyTarget = (rowKey: string | null, target: ComponentTarget) => {
    setTargets((previous) =>
      new Map(previous).set(targetKey(target.kind, target.id), { ...target, resolved: true }),
    );
    // The unit always resets to the target's own: a unit carried over from the
    // previous target could be of a type this one cannot convert from.
    if (rowKey === 'NEW') {
      setRows((previous) => [
        ...previous,
        {
          key: nextKey(),
          targetKind: target.kind,
          targetId: target.id,
          unitId: target.unitId ?? '',
          quantity: '1',
          wastage: '0',
          isOptional: false,
        },
      ]);
    } else if (rowKey) {
      patchRow(rowKey, {
        targetKind: target.kind,
        targetId: target.id,
        unitId: target.unitId ?? '',
      });
    }
    setPickerFor(null);
  };

  // -------------------------------------------------------------- validation

  const rowError = useCallback(
    (row: Row): string | null => {
      const target = targetOf(row);
      if (!row.targetId) return t('platform.recipes.editor.errors.chooseComponent');
      if (row.targetKind === 'SUB' && row.targetId === recipeId) {
        return t('platform.recipes.editor.errors.selfReference');
      }
      if (row.targetKind === 'SUB' && target?.resolved && !target.unitId) {
        return t('platform.recipes.editor.errors.subNoActiveVersion');
      }
      if (!row.unitId) return t('platform.recipes.editor.errors.chooseUnit');
      const allowed = unitsFor(row);
      if (allowed.length && !allowed.some((unit) => unit.id === row.unitId)) {
        return t('platform.recipes.editor.errors.unitMismatch');
      }
      if (!isDecimalWithScale(row.quantity, 3) || isZeroDecimal(row.quantity)) {
        return t('platform.recipes.editor.errors.badQuantity');
      }
      // Stored as a fraction in Decimal(5,4): at most 999.99% survives the column.
      const wastage = row.wastage.trim();
      if (wastage && (!isDecimalWithScale(wastage, 2) || !/^\d{1,3}(\.\d+)?$/.test(wastage))) {
        return t('platform.recipes.editor.errors.badWastage');
      }
      return null;
    },
    [recipeId, t, targetOf, unitsFor],
  );

  const yieldQuantityError = useMemo(
    () =>
      isDecimalWithScale(yieldQuantity, 3) && !isZeroDecimal(yieldQuantity)
        ? null
        : t('platform.recipes.editor.errors.badYield'),
    [t, yieldQuantity],
  );
  const yieldUnitError = yieldUnitId ? null : t('platform.recipes.editor.errors.chooseUnit');

  const rowErrors = useMemo(
    () => new Map(rows.map((row) => [row.key, rowError(row)])),
    [rowError, rows],
  );
  const complete =
    rows.length > 0 &&
    !yieldQuantityError &&
    !yieldUnitError &&
    [...rowErrors.values()].every((message) => !message);

  /**
   * Why neither save button will fire. `@ArrayMinSize(1)` on the version DTO
   * and `activateVersion()`'s "a version with no components would consume
   * nothing" are the same requirement seen from both ends, so an empty BOM is
   * named rather than left as a dead button.
   */
  const saveBlocked = complete
    ? null
    : !rows.length
      ? t('platform.recipes.editor.errors.noComponents')
      : t('platform.recipes.editor.errors.incomplete');

  // ------------------------------------------------------------------ payload

  const payload = useMemo<VersionPayload | null>(() => {
    if (!complete) return null;
    const components: ComponentPayload[] = rows.map((row, index) => ({
      // One key or the other. There is no branch in which both are written.
      ...(row.targetKind === 'ITEM'
        ? { inventoryItemId: row.targetId }
        : { subRecipeId: row.targetId }),
      unitId: row.unitId,
      quantity: row.quantity.trim(),
      wastagePercent: percentToFraction(row.wastage.trim() || '0'),
      isOptional: row.isOptional,
      sortOrder: index,
    }));
    // `notes` is deliberately absent: it is saved, but it must not re-trigger the
    // cost preview, and the preview endpoint whitelists its own fields.
    return { yieldUnitId, yieldQuantity: yieldQuantity.trim(), components };
  }, [complete, rows, yieldQuantity, yieldUnitId]);

  // ------------------------------------------------------------- cost preview

  const payloadKey = payload ? JSON.stringify(payload) : '';

  useEffect(() => {
    if (!open || !payloadKey) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    const sequence = (previewSeq.current += 1);
    const timer = window.setTimeout(() => {
      setPreviewLoading(true);
      api
        .post<CostPreview>('/recipes/cost-preview', {
          ...(JSON.parse(payloadKey) as Omit<VersionPayload, 'notes'>),
          recipeId,
          // Cost for one sold unit: components are per batch, so this is the
          // figure the yield divides — a wrong yield shows up here at once.
          quantity: '1',
          includeOptional,
        })
        .then(({ data }) => {
          if (sequence !== previewSeq.current) return;
          setPreview(data);
          setPreviewError(null);
        })
        .catch((e) => {
          if (sequence !== previewSeq.current) return;
          setPreview(null);
          // The BOM cycle guard, an unconvertible unit and a sub-recipe with no
          // active version all answer here, in the server's own words.
          setPreviewError(apiMessage(e, t('platform.recipes.explodeFailed')));
        })
        .finally(() => {
          if (sequence === previewSeq.current) setPreviewLoading(false);
        });
    }, 500);

    return () => window.clearTimeout(timer);
  }, [includeOptional, open, payloadKey, recipeId, t]);

  // --------------------------------------------------------------------- save

  const save = async (activate: boolean) => {
    if (!payload) {
      setError(t('platform.recipes.editor.errors.incomplete'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: VersionPayload = { ...payload, notes: notes.trim() };
      const saved = draft
        ? (await api.patch<{ id: string }>(`/recipes/versions/${draft.id}`, body)).data
        : (await api.post<{ id: string }>(`/recipes/${recipeId}/versions`, body)).data;
      if (activate) await api.post(`/recipes/versions/${saved.id}/activate`, {});
      toast.success(
        t(activate ? 'platform.recipes.editor.savedAndActivated' : 'platform.recipes.editor.saved'),
      );
      onSaved();
    } catch (e) {
      setError(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setSaving(false);
    }
  };

  const title = draft
    ? t('platform.recipes.editor.editTitle', { name: recipeName, version: draft.version })
    : seed
      ? t('platform.recipes.editor.copyTitle', { name: recipeName, version: seed.version })
      : t('platform.recipes.editor.newTitle', { name: recipeName });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={t('platform.recipes.editor.subtitle')}
      className="max-w-5xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void save(false)}
            disabled={saving || !payload}
            title={saveBlocked ?? undefined}
          >
            {t('platform.recipes.editor.saveDraft')}
          </Button>
          <Button
            onClick={() => void save(true)}
            disabled={saving || !payload}
            title={saveBlocked ?? undefined}
          >
            {t('platform.recipes.editor.saveAndActivate')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* ------------------------------------------- why this is a new draft */}
        {seed && !draft && (
          <p className="rounded-lg bg-sky-50 dark:bg-sky-950/50 p-3 text-sm text-sky-900 dark:text-sky-200">
            {t('platform.recipes.editor.immutableNotice', {
              version: seed.version,
              status: t(`platform.recipes.versionStatus.${seed.status}`),
            })}
          </p>
        )}
        {draft && (
          <p className="rounded-lg bg-subtle p-3 text-sm text-ink-muted">
            {t('platform.recipes.editor.draftNotice', { version: draft.version })}
          </p>
        )}

        {error && (
          <p className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-950/50 p-3 text-sm text-red-700 dark:text-red-400">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            {error}
          </p>
        )}

        {/* ------------------------------------------------------------ yield */}
        <div className="grid gap-3 sm:grid-cols-3">
          <Field
            label={t('platform.recipes.editor.yieldQuantity')}
            hint={t('platform.recipes.editor.yieldHint')}
            error={yieldQuantityError ?? undefined}
            required
          >
            <Input
              inputMode="decimal"
              value={yieldQuantity}
              onChange={(e) => setYieldQuantity(e.target.value)}
            />
          </Field>
          <Field
            label={t('platform.recipes.editor.yieldUnit')}
            error={yieldUnitError ?? undefined}
            required
          >
            <Select value={yieldUnitId} onChange={(e) => setYieldUnitId(e.target.value)}>
              <option value="">{t('platform.recipes.editor.chooseUnit')}</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.code} — {unit.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('platform.procurement.notes')}>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>

        {/* ------------------------------------------------------- components */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">{t('platform.recipes.editor.components')}</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPickerFor(pickerFor === 'NEW' ? null : 'NEW')}
            >
              <Plus className="h-4 w-4" />
              {t('platform.recipes.editor.addComponent')}
            </Button>
          </div>

          {unitsApi.loading && !units.length ? (
            <LoadingState label={t('common.loading')} />
          ) : unitsApi.error ? (
            <ErrorState message={unitsApi.error} onRetry={unitsApi.reload} />
          ) : (
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('platform.recipes.component')}</TH>
                    <TH className="w-28">{t('common.quantity')}</TH>
                    <TH className="w-40">{t('platform.recipes.editor.unit')}</TH>
                    <TH className="w-28">{t('platform.recipes.editor.wastage')}</TH>
                    <TH className="w-24">{t('platform.recipes.optional')}</TH>
                    <TH className="w-28 text-end">{t('platform.actions')}</TH>
                  </TR>
                </THead>
                <tbody>
                  {!rows.length && (
                    <TableEmpty colSpan={6}>
                      <EmptyState
                        title={t('platform.recipes.editor.noRows')}
                        hint={t('platform.recipes.editor.noRowsHint')}
                      />
                    </TableEmpty>
                  )}

                  {rows.map((row, index) => {
                    const target = targetOf(row);
                    const allowed = unitsFor(row);
                    const message = rowErrors.get(row.key);
                    return (
                      <TR key={row.key}>
                        <TD>
                          <button
                            type="button"
                            className="text-start font-medium text-accent underline"
                            onClick={() => setPickerFor(pickerFor === row.key ? null : row.key)}
                          >
                            {target?.name ?? t('platform.recipes.unknownComponent')}
                          </button>
                          <Badge tone={row.targetKind === 'SUB' ? 'info' : 'neutral'} className="ms-2">
                            {row.targetKind === 'SUB'
                              ? t('platform.recipes.subRecipe')
                              : t('platform.recipes.editor.ingredient')}
                          </Badge>
                          {target?.hint && (
                            <div className="text-xs text-ink-muted">{target.hint}</div>
                          )}
                          {message && <div className="mt-1 text-xs text-red-600 dark:text-red-400">{message}</div>}
                        </TD>
                        <TD>
                          <Input
                            inputMode="decimal"
                            value={row.quantity}
                            aria-label={t('common.quantity')}
                            onChange={(e) => patchRow(row.key, { quantity: e.target.value })}
                          />
                        </TD>
                        <TD>
                          <Select
                            value={row.unitId}
                            aria-label={t('platform.recipes.editor.unit')}
                            disabled={!allowed.length}
                            onChange={(e) => patchRow(row.key, { unitId: e.target.value })}
                          >
                            {!allowed.length && (
                              <option value={row.unitId}>
                                {unitById.get(row.unitId)?.code ??
                                  t('platform.recipes.editor.chooseUnit')}
                              </option>
                            )}
                            {allowed.map((unit) => (
                              <option key={unit.id} value={unit.id}>
                                {unit.code}
                              </option>
                            ))}
                          </Select>
                        </TD>
                        <TD>
                          <Input
                            inputMode="decimal"
                            value={row.wastage}
                            aria-label={t('platform.recipes.editor.wastage')}
                            onChange={(e) => patchRow(row.key, { wastage: e.target.value })}
                          />
                        </TD>
                        <TD>
                          <input
                            type="checkbox"
                            className="h-5 w-5"
                            aria-label={t('platform.recipes.optional')}
                            checked={row.isOptional}
                            onChange={(e) => patchRow(row.key, { isOptional: e.target.checked })}
                          />
                        </TD>
                        <TD>
                          <div className="flex justify-end gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={t('platform.recipes.editor.moveUp')}
                              disabled={index === 0}
                              onClick={() => moveRow(index, -1)}
                            >
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={t('platform.recipes.editor.moveDown')}
                              disabled={index === rows.length - 1}
                              onClick={() => moveRow(index, 1)}
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={t('common.delete')}
                              onClick={() =>
                                setRows((previous) => previous.filter((r) => r.key !== row.key))
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TD>
                      </TR>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          )}

          {pickerFor && (
            <RecipeComponentPicker
              excludeRecipeId={recipeId}
              onPick={(target) => applyTarget(pickerFor, target)}
              onCancel={() => setPickerFor(null)}
            />
          )}
        </div>

        {/* -------------------------------------------------------- cost panel */}
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-3">
            <CardTitle className="text-sm">{t('platform.recipes.editor.costTitle')}</CardTitle>
            <label className="flex items-center gap-2 text-xs text-ink-muted">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={includeOptional}
                onChange={(e) => setIncludeOptional(e.target.checked)}
              />
              {t('platform.recipes.editor.includeOptional')}
            </label>
          </CardHeader>
          <CardContent className="space-y-3">
            {!payload ? (
              <EmptyState
                title={t('platform.recipes.editor.costPending')}
                hint={t('platform.recipes.editor.costPendingHint')}
              />
            ) : previewError ? (
              <ErrorState message={previewError} />
            ) : !preview || previewLoading ? (
              <LoadingState label={t('common.loading')} />
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-ink-muted">
                      {t('platform.recipes.editor.perUnit', {
                        unit: unitById.get(yieldUnitId)?.code ?? '',
                      })}
                    </p>
                    <p className="text-xl font-semibold tabular-nums">{format(preview.unitCost)}</p>
                    <p className="text-xs text-ink-muted tabular-nums">
                      {preview.unitCost}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-ink-muted">
                      {t('platform.recipes.editor.perBatch', {
                        qty: formatQuantity(preview.yieldQuantity),
                        unit: unitById.get(yieldUnitId)?.code ?? '',
                      })}
                    </p>
                    <p className="text-xl font-semibold tabular-nums">{format(preview.batchCost)}</p>
                  </div>
                </div>

                <TableWrap>
                  <Table>
                    <THead>
                      <TR>
                        <TH>{t('platform.recipes.component')}</TH>
                        <TH className="text-end">{t('common.quantity')}</TH>
                        <TH className="text-end">{t('platform.procurement.unitCost')}</TH>
                        <TH className="text-end">{t('platform.recipes.lineCost')}</TH>
                      </TR>
                    </THead>
                    <tbody>
                      {!preview.components.length && (
                        <TableEmpty colSpan={4}>
                          <EmptyState title={t('platform.recipes.noComponents')} />
                        </TableEmpty>
                      )}
                      {preview.components.map((line) => (
                        <TR key={`${line.inventoryItemId}-${line.via.join('-')}`}>
                          <TD>
                            {line.name}
                            {line.via.length > 0 && (
                              <div className="text-xs text-ink-muted">
                                {t('platform.recipes.viaDepth', { count: line.via.length })}
                              </div>
                            )}
                          </TD>
                          <TD className="text-end tabular-nums">
                            {formatQuantity(line.quantity)} {unitById.get(line.unitId)?.code ?? ''}
                          </TD>
                          <TD className="text-end tabular-nums">{format(line.unitCost)}</TD>
                          <TD className="text-end font-medium tabular-nums">{format(line.cost)}</TD>
                        </TR>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Dialog>
  );
}
